/**
 * Bandwidth service client — typed API calls for bandwidth snapshots and analytics.
 */

import { apiClient } from '@/services/apiClient';
import type {
  BandwidthSnapshot,
  PaginatedResponse,
  SnapshotTrendItem,
  AggregateAnalytics,
  CarrierDistributionItem,
  UnderservedLocation,
  BandwidthRankingComparison,
} from '@/types';

// ─── Types ─────────────────────────────────────────────────────────────

export interface SnapshotListParams {
  page?: number;
  pageSize?: number;
  siteNumber?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface CreateSnapshotInput {
  siteNumber: string;
  snapshotDate: string;
  bandwidthByCarrier?: string | null;
  wan1SpeedMbps?: number | null;
  wan2SpeedMbps?: number | null;
  totalBandwidthMbps?: number | null;
  primaryCircuitDetails?: string | null;
  secondaryCircuitDetails?: string | null;
}

export type UpdateSnapshotInput = Partial<CreateSnapshotInput> & { updatedAt: string };

export interface BulkImportResult {
  imported: number;
  updated: number;
  errors: Array<{ row: number; error: string }>;
}

// ─── Service ───────────────────────────────────────────────────────────

export const bandwidthService = {
  // CRUD
  list(params?: SnapshotListParams): Promise<PaginatedResponse<BandwidthSnapshot>> {
    return apiClient.get('bandwidth-snapshots', params as Record<string, string | number | boolean | undefined>);
  },

  getById(snapshotId: number): Promise<BandwidthSnapshot> {
    return apiClient.get(`bandwidth-snapshots/${snapshotId}`);
  },

  create(input: CreateSnapshotInput): Promise<BandwidthSnapshot> {
    return apiClient.post('bandwidth-snapshots', input);
  },

  update(snapshotId: number, input: UpdateSnapshotInput): Promise<BandwidthSnapshot> {
    return apiClient.put(`bandwidth-snapshots/${snapshotId}`, input);
  },

  delete(snapshotId: number): Promise<void> {
    return apiClient.delete(`bandwidth-snapshots/${snapshotId}`);
  },

  bulkImport(snapshots: CreateSnapshotInput[]): Promise<BulkImportResult> {
    return apiClient.post('bandwidth-snapshots/import', { snapshots });
  },

  // Analytics
  getTrend(siteNumber: string): Promise<SnapshotTrendItem[]> {
    return apiClient.get(`bandwidth-snapshots/trend/${siteNumber}`);
  },

  getAggregateAnalytics(): Promise<AggregateAnalytics> {
    return apiClient.get('bandwidth-snapshots/analytics/aggregate');
  },

  getCarrierDistribution(): Promise<CarrierDistributionItem[]> {
    return apiClient.get('bandwidth-snapshots/analytics/carrier-distribution');
  },

  getUnderservedLocations(thresholdMbps?: number): Promise<UnderservedLocation[]> {
    const params = thresholdMbps !== undefined ? { thresholdMbps } : undefined;
    return apiClient.get('bandwidth-snapshots/analytics/underserved', params as Record<string, string | number | boolean | undefined>);
  },

  getRankingComparison(): Promise<BandwidthRankingComparison[]> {
    return apiClient.get('bandwidth-snapshots/analytics/ranking-comparison');
  },
};
